import { CallPermisionsResponseType } from '../../home/v1/typings/chat';

export const enum WACallsEventTypes {
  INCOMMING_CALL = 'incoming-call',
  CALL_TERMINATE = 'call-terminate',
  CALL_PICKED = 'call-picked',
  CALL_STATUS_UPDATES = 'call-status-updates',
  CALL_PERMISSION = 'call-permission-state',
  CALL_RECONNECTED = 'call-reconnected',
}

export const enum CallUpdatesTypes {
  RINGING = 'RINGING',
  ACCEPTED = 'ACCEPTED',
}

export type WACallPayload =
  | WACallIncomingPayload
  | WACallTerminatePayload
  | WACallPickedPayload
  | WACallStatusUpdatesPayload
  | WACallPermissionPayload
  | WACallReconnectedPayload;

export interface WACallIncomingPayload {
  event: WACallsEventTypes.INCOMMING_CALL;
  customerName: string;
  customerNumber: string;
  wabaNumber: string;
  wabaName: string;
  callId: string;
  sdp: string;
  sdpType: string;
  startTimestamp: string;
  expiryTimestamp: number;
}

export interface WACallTerminatePayload {
  event: WACallsEventTypes.CALL_TERMINATE;
  callId: string;
  duration: number;
  direction: 'USER_INITIATED';
  startTime: string;
  endTime: string;
  expiryTime?: number;
  endedBy?: 'SYSTEM' | 'USER' | null;
}

export interface WACallTerminateCustomPayload {
  callId: string;
  duration: number;
  direction: 'USER_INITIATED';
  startTime: string;
  endTime: string;
  expiryTime?: number;
  endedBy?: 'SYSTEM' | 'USER' | null;
}

export interface WACallPickedPayload {
  event: WACallsEventTypes.CALL_PICKED;
  callId: string;
  userId: string;
  direction?: 'incoming' | 'outgoing';
  sdp?: string;
  sdpType?: string;
  expiryTime?: number;
}

export type WACallPermissionPayload = {
  event: WACallsEventTypes.CALL_PERMISSION;
  wabaNumber: string;
  customerNumber: string;
  callPermissionData: CallPermisionsResponseType;
};

export interface WACallReconnectedPayload {
  event: WACallsEventTypes.CALL_RECONNECTED;
  callId: string;
  deviceId: string;
}

export interface WACallStatusUpdatesPayload {
  event: WACallsEventTypes.CALL_STATUS_UPDATES;
  callId: string;
  status: CallUpdatesTypes;
  customerName: string;
  customerNumber: string;
  timestamp: string;
}
export enum CallDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}
export enum CallStatusFromBE {
  INITIATED = 'INITIATED',
  IN_PROGRESS = 'IN_PROGRESS',
  END = 'END',
  MISSED = 'MISSED',
  FAILED = 'FAILED',
  REJECT = 'REJECT',
}

export enum CallStatus {
  INITIATED = 'INITIATED',
  Connecting = 'connecting',
  Ringing = 'ringing',
  IN_PROGRESS = 'IN_PROGRESS',
  END = 'END',
  MISSED = 'MISSED',
  FAILED = 'FAILED',
  REJECT = 'REJECT',
}

export type CallDisconnectStatus = CallStatus.END | CallStatus.MISSED | CallStatus.FAILED;
export interface Call {
  customerName: string;
  customerNumber: string;
  customerId?: string;
  integrationId?: string;
  wabaNumber: string;
  wabaName: string;
  callId: string;
  tempCallId?: string; // temporary callId for outgoing calls until we get the real callId from backend
  sdp: string;
  sdpType: string;
  status: CallStatus;
  shouldForceDisconnect?: boolean;
  shouldConnect?: boolean;
  reconnect?: boolean;
  dateCreated?: string; // time when call was created (INITIATED)
  expiryTimestamp?: number; // timestamp in ms when the call will expire
  direction: CallDirection;
  switchCall?: boolean;
}

export interface CallWithTimestamp extends Call {
  callStartTime?: number;
}

export enum SdpPayloadType {
  OFFER = 'offer',
  ANSWER = 'answer',
}

export type SdpPayload = {
  sdp: string;
  type: SdpPayloadType;
};

export type CallFromUpdatesApiMetadata = {
  offerForCallee: SdpPayload;
};
export type CallFromUpdatesApi = {
  id: string;
  customerName: string;
  customerNumber: string;
  wabaNumber: string;
  wabaName: string;
  callId: string;
  customerId: string;
  integrationId: string;
  orgId: string;
  pickedBy: string;
  currentOnCallUser: string;
  status: CallStatus;
  recordingUrl: string | null;
  callOriginType: CallDirection;
  callStartedAt: string | null; // this will be null for INITIATED calls
  callEndedAt: string | null;
  dateCreated: string;
  dateUpdated: string;
  chatMessageId: string;
  durationInSeconds: number | null; // this will be only for ENDED calls
  metadata: CallFromUpdatesApiMetadata;
  deviceId?: string;
};

export interface CallPopupProps {
  callData: Call;
  handleAcceptCall: (call: Call) => void;
  onAccept: (state: CallStatus) => void;
  onReject: () => void;
  onDisconnect: () => void;
  onMinimize?: () => void;
  style?: React.CSSProperties;
}

export interface AnswerIncommingCallApiType {
  callId: string;
  sdp: string;
  sdpType: string;
  reconnect?: boolean;
}

export type ReconnectSDPResponse = {
  success: boolean;
  message: string;
  data: SdpPayload;
  callStartedAt: string;
};

export interface CreateOutgoingCallApiRequest {
  integrationId: string;
  customerNumber: string;
  sdp: string;
  sdpType: 'offer' | 'answer' | string;
}

export interface CreateOutgoingCallApiResponse {
  success: boolean;
  data: {
    callId: string;
  };
  customer: {
    name: string;
    phoneNumber: string;
  };
  answer: SdpPayload;
}

export interface MakeOutgoingCallParams {
  customerName: string;
  customerNumber: string;
  wabaNumber: string;
  wabaName: string;
  integrationId: string;
  customerId?: string;
}

export interface UpdateOutgoingCallIdParams {
  tempCallId: string;
  newCallId: string;
}

export interface UseCallOperationsProps {
  onCallPicked?: (callId: string) => void;
  onCallCreated?: (callId: string) => void;
  onCallRemoved?: (callId: string) => void;
}
